import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useAuth0, Auth0Provider } from '@auth0/auth0-react';
import { connect } from 'react-redux';
import cookie from 'js-cookie';
import { connectedRouterRedirect } from 'redux-auth-wrapper/history3/redirect';
import { Alert, Button, ProgressBar } from 'react-bootstrap';
import {
  saveAuthDataToStore,
  resetAuthState,
  setAuthLoading,
  loadCurrentUserData,
  checkAuth,
} from '../redux/modules/auth';
import NotificationModal from './common/NotificationModal';

const AuthContextDefault = {
  userId: undefined,
  login: undefined,
  reload: undefined,
  logout: undefined,
};

export const AuthContext = createContext(AuthContextDefault);

const returnTo = window.location.origin;

const auth0Props = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
  redirectUri: window.location.origin,
  returnTo,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  scope: process.env.REACT_APP_AUTH0_SCOPE,
};

const Auth0Wrap = ({ children, user, saveAuthDataToStore, resetAuthState, setAuthLoading }) => {
  const {
    user: auth0user,
    error,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout: auth0Logout,
  } = useAuth0();

  // Indicates that auth data is loading
  const [reloading, setReloading] = useState(false);

  // reload auth data from server once auth0 user is available
  const [autoReload, setAutoReload] = useState(true);

  useEffect(() => {
    if (!auth0user) {
      setAutoReload(true);
    }
  }, [auth0user]);

  const login = loginWithRedirect;

  const logout = useMemo(
    () =>
      auth0user || user || error
        ? () => {
            resetAuthState();
            if (auth0Logout) auth0Logout({ returnTo });
          }
        : undefined,
    [auth0user, user, error, auth0Logout, resetAuthState],
  );

  const [tokenExp, setTokenExp] = useState();
  const [authExpired, resetAuthExpired] = watchAuthExpired(tokenExp);
  const reload = useMemo(
    () =>
      auth0user
        ? async () => {
            setTokenExp(undefined);
            resetAuthExpired();

            if (error) {
              resetAuthState();
              auth0Logout({
                returnTo,
              });
              return;
            }
            setReloading(true);
            setAutoReload(false);
            await getAccessTokenSilently();
            const claims = await getIdTokenClaims();
            const token = claims.__raw;
            setTokenExp(claims.exp);
            cookie.set('auth0Token', token);
            try {
              const { user } = await loadCurrentUserData();
              saveAuthDataToStore(undefined, {
                user,
              });
              setReloading(false);
            } catch (error) {
              setReloading(false);
              saveAuthDataToStore(error);
            }
          }
        : undefined,
    [
      resetAuthExpired,
      auth0user,
      error,
      auth0Logout,
      getAccessTokenSilently,
      getIdTokenClaims,
      saveAuthDataToStore,
      resetAuthState,
    ],
  );

  useEffect(() => {
    setAuthLoading({ isLoading: reloading || isLoading || (!!auth0user && autoReload) });
  }, [reloading, isLoading, setAuthLoading, auth0user, autoReload]);

  useEffect(() => {
    if (error) {
      saveAuthDataToStore(error);
    }
  }, [error, saveAuthDataToStore]);

  useEffect(() => {
    if (reload) {
      reload();
    }
  }, [reload]);

  const contextValue = useMemo(
    () => ({
      userId: auth0user ? auth0user.sub : undefined,
      login,
      reload,
      logout,
      authExpired,
    }),
    [auth0user, login, reload, logout, authExpired],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const AuthExpiredPopup = () => {
  const { authExpired, login } = useContext(AuthContext) || {};
  const [hide, setHide] = useState(false);

  if (!authExpired || hide) return null;

  const relogin = () => {
    login({
      prompt: 'login',
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, .95)',
        display: 'flex',
        flexFlow: 'column',
        padding: 12,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          padding: 24,
          background: 'white',
          boxShadow: '0 0 76px 70px white',
        }}
      >
        <Alert bsStyle="warning">
          <h4>Your auth session expired</h4>
          <p>To continue, you would need to login again.</p>
          <p>
            <Button bsStyle="primary" bsSize="large" onClick={relogin}>
              Login
            </Button>
          </p>
        </Alert>
        <div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setHide(true)}
            style={{ opacity: 0.4, textAlign: 'right' }}
          >
            Hide this message
          </div>
        </div>
      </div>
    </div>
  );
};

export const AuthErrorPopup = ({ error, isLoading }) => {
  const { reload, logout } = useContext(AuthContext);

  return (
    <NotificationModal
      title="Authentication failed"
      show={!!error}
      confirmText="Try again"
      disabledConfirm={isLoading || !reload}
      onConfirm={reload}
      cancelText="Logout"
      disabledCancel={!logout}
      onCancel={logout}
    >
      <p>
        This may happen due to network connectivity issue, or we may have trouble on our end. You
        can try:
      </p>
      <ul>
        <li>Check your network connection and try again</li>
        <li>Logout, and then login back</li>
        <li>{error}</li>
      </ul>
    </NotificationModal>
  );
};

export const AuthProviderComponent = props => {
  const { error, isLoading, saveAuthDataToStore, resetAuthState, setAuthLoading, children } = props;

  return (
    <Auth0Provider {...auth0Props}>
      <Auth0Wrap
        saveAuthDataToStore={saveAuthDataToStore}
        resetAuthState={resetAuthState}
        setAuthLoading={setAuthLoading}
      >
        <AuthErrorPopup error={error} isLoading={isLoading} />
        <AuthExpiredPopup />
        {children}
      </Auth0Wrap>
    </Auth0Provider>
  );
};

export const AuthProvider = connect(
  ({ auth: { user, isLoading, error } }) => ({
    user,
    isLoading,
    error,
  }),
  {
    saveAuthDataToStore,
    resetAuthState,
    setAuthLoading,
  },
)(AuthProviderComponent);

export const makeRouterRedirects = () => {
  const authDefaults = {
    authenticatingSelector: state => !state.auth.initialized || state.auth.isLoading,
    AuthenticatingComponent: () => <ProgressBar now={100} active={true} />,
  };

  // Authenticated routes
  // eslint-disable-next-line no-unused-vars
  const userAuth = connectedRouterRedirect({
    ...authDefaults,
    redirectPath: '/',
    authenticatedSelector: state =>
      !authDefaults.authenticatingSelector(state) && !!state.auth.user,
    wrapperDisplayName: 'UserIsAuthenticated',
  });

  // eslint-disable-next-line no-unused-vars
  const dealerAdminAuth = connectedRouterRedirect({
    ...authDefaults,
    redirectPath: '/',
    authenticatedSelector: state =>
      !authDefaults.authenticatingSelector(state) &&
      !!(
        !!state.auth.user &&
        (_.get(state.auth.user, 'dealerRel.dealerAdmin') ||
          _.get(state.auth.user, 'dealerRel.prcoAdmin'))
      ),
    wrapperDisplayName: 'AdminIsAuthenticated',
  });

  // eslint-disable-next-line no-unused-vars
  const prcoAdminAuth = connectedRouterRedirect({
    ...authDefaults,
    redirectPath: '/',
    authenticatedSelector: state =>
      !authDefaults.authenticatingSelector(state) &&
      !!state.auth.user &&
      _.get(state.auth.user, 'dealerRel.prcoAdmin'),
    wrapperDisplayName: 'AdminIsAuthenticated',
  });

  return {
    userAuth,
    dealerAdminAuth,
    prcoAdminAuth,
  };
};

function watchAuthExpired(tokenExpTimestamp, heartbeatInterval = 3000) {
  /** undefined | false | true */
  const [authExpired, setAuthExpired] = useState();

  useEffect(() => {
    if (authExpired) return undefined;

    if (!tokenExpTimestamp) return undefined;

    const lifetime = tokenExpTimestamp * 1000 - +new Date();

    const timeout = setTimeout(() => setAuthExpired(true), Math.max(lifetime, 1));

    return () => clearTimeout(timeout);
  }, [authExpired, tokenExpTimestamp]);

  useEffect(() => {
    if (authExpired !== undefined) return undefined;

    if (!tokenExpTimestamp) return undefined;

    let cancelled = false;
    const timeout = setTimeout(() => {
      if (cancelled) return undefined;
      checkAuth().then(result => {
        if (!cancelled) setAuthExpired(!!result);
      });
    }, 3000);

    return () => {
      clearTimeout(timeout);
      cancelled = true;
    };
  }, [authExpired, tokenExpTimestamp]);

  useEffect(() => {
    if (authExpired !== false) return undefined;

    if (!tokenExpTimestamp) return undefined;

    const timeout = setTimeout(() => setAuthExpired(undefined), heartbeatInterval);

    return () => clearTimeout(timeout);
  }, [authExpired, tokenExpTimestamp, heartbeatInterval]);

  const reset = useCallback(() => setAuthExpired(undefined), []);

  return [authExpired, reset];
}
